import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import PWAInstallNudge from "@/components/PWAInstallNudge";
import { useAuth } from "@/context/AuthContext";
import { api, inr, formatErr } from "@/lib/api";
import AllergenBadge from "@/components/AllergenBadge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Wallet, Plus, ScanLine, BarChart3, UtensilsCrossed, Bell, AlertTriangle, ArrowRight, Sparkles,
  Shield, Settings2, CheckCircle2, Loader2, PartyPopper, Zap, TrendingUp, BadgeIndianRupee, X, CreditCard, Calendar, FileText,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TAGS = ["veg", "vegan", "gf", "nuts", "dairy", "spicy", "egg"];
const QUICK_AMOUNTS = [200, 500, 1000, 2000, 5000];

export default function ParentDashboard() {
  const { user, school, refresh } = useAuth();
  const instashopOn = school?.instashop_enabled === true;
  const [children, setChildren] = useState([]);
  const [plans, setPlans] = useState([]);
  const [report, setReport] = useState(null);
  const [activeChild, setActiveChild] = useState(null);
  const [festivals, setFestivals] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [orderingSpecial, setOrderingSpecial] = useState(null);

  // Top-up modal
  const [topup, setTopup] = useState({ open: false, target: null, amount: 500, processing: false });

  // Dietary controls modal
  const [dietary, setDietary] = useState({ open: false, target: null, data: null });

  // Auto-topup modal
  const [autoTopup, setAutoTopup] = useState({ open: false, target: null, data: null });

  // Subscription payment modal
  const [subPay, setSubPay] = useState({ open: false, plan: null, processing: false, invoice: null });

  const [savings, setSavings] = useState(null);
  const [upcomingBookings, setUpcomingBookings] = useState([]);

  const load = async () => {
    if (user?.role === "parent") {
      const [c, p, f, sp] = await Promise.all([
        api.get("/parent/children"), api.get("/meal-plans"), api.get("/festivals/active"),
        api.get("/specials/active"),
      ]);
      setChildren(c.data); setPlans(p.data); setFestivals(f.data); setSpecials(sp.data);
      if (c.data.length && !activeChild) setActiveChild(c.data[0].id);
    } else {
      // Student logged in directly — treat self as the child
      setChildren([{ ...user }]);
      setActiveChild(user.id);
      const [p, f, sp] = await Promise.all([
        api.get("/meal-plans"), api.get("/festivals/active"), api.get("/specials/active"),
      ]);
      setPlans(p.data); setFestivals(f.data); setSpecials(sp.data);
    }
  };

  useEffect(() => { load(); }, [user]);

  useEffect(() => {
    if (!activeChild) return;
    api.get(`/parent/savings/${activeChild}`).then(r => setSavings(r.data)).catch(() => setSavings(null));
    // Fetch plan bookings + special bookings
    api.get(`/meal-plans/student/${activeChild}`).then(r => {
      const planBookings = (r.data || []).map(b => ({ ...b, booking_type: "plan" }));
      // Also get special purchase transactions to show booked specials
      const uid = user?.id;
      api.get(`/wallet/transactions?user_id=${uid}`).then(tr => {
        const specialTxns = (tr.data || []).filter(t => t.type === "special_purchase" && t.ref_order_id);
        // Load specials to get dates
        api.get("/specials/active").then(sp => {
          const today = new Date().toISOString().slice(0, 10);
          const specialBookings = specialTxns.map(t => {
            const special = (sp.data || []).find(s => s.id === t.ref_order_id);
            if (!special || (special.scheduled_date && special.scheduled_date < today)) return null;
            return {
              id: t.id,
              plan_name: special.name,
              plan_type: "special",
              start_date: special.scheduled_date,
              status: "booked",
              booking_type: "special",
              image_url: special.image_url || null,
              nutrition: special.nutrition || null,
            };
          }).filter(Boolean);
          setUpcomingBookings([...planBookings, ...specialBookings].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || "")));
        }).catch(() => setUpcomingBookings(planBookings));
      }).catch(() => setUpcomingBookings(planBookings));
    }).catch(() => setUpcomingBookings([]));
    if (user?.role === "parent") {
      api.get(`/parent/consumption/${activeChild}?days=7`).then(r => setReport(r.data));
    } else {
      api.get("/orders/me").then(r => {
        const orders = r.data;
        const daily = {};
        let total = 0;
        for (const o of orders) {
          const d = o.created_at.slice(0, 10);
          daily[d] = (daily[d] || 0) + o.total;
          total += o.total;
        }
        setReport({
          total_spent: total, order_count: orders.length,
          daily: Object.entries(daily).map(([date, amount]) => ({ date, amount })).slice(-7),
          top_items: [], orders,
        });
      });
    }
  }, [activeChild, user]);

  const doTopup = async (forceFail = false) => {
    setTopup(t => ({ ...t, processing: true }));
    try {
      const cfg = await api.get("/payments/config");
      const { data } = await api.post("/payments/cashfree/create-order", {
        amount: Number(topup.amount), target_user_id: topup.target,
      });
      if (cfg.data.cashfree_configured && !data.is_mock && window.Cashfree && data.payment_session_id) {
        // Real Cashfree drop-in
        const cf = window.Cashfree({ mode: cfg.data.env === "PRODUCTION" ? "production" : "sandbox" });
        const result = await cf.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_modal" });
        if (result.error) {
          toast.error(result.error.message || "Payment cancelled");
        } else {
          toast.success("Payment successful — wallet credited");
          setTopup({ open: false, target: null, amount: 500, processing: false });
          refresh(); load();
          return;
        }
        setTopup(t => ({ ...t, processing: false }));
        return;
      }
      // Mock flow — simulate either success or failure so demos can show both paths.
      if (forceFail) {
        const r = await api.post(`/payments/cashfree/mock-fail/${data.cf_order_id}?reason=user_cancel`);
        toast.error(`Payment failed (${r.data.reason}). No money was charged.`);
        setTopup({ open: false, target: null, amount: 500, processing: false });
        return;
      }
      await api.post(`/payments/cashfree/mock-success/${data.cf_order_id}`);
      toast.success(`₹${topup.amount} added to wallet`);
      setTopup({ open: false, target: null, amount: 500, processing: false });
      refresh(); load();
    } catch (e) {
      toast.error(formatErr(e));
      setTopup(t => ({ ...t, processing: false }));
    }
  };

  const subscribe = (planId, sid) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;
    const taxRate = plan.tax_rate || 5;
    const taxAmount = Math.round(plan.price * taxRate) / 100;
    const total = plan.price + taxAmount;
    setSubPay({ open: true, plan, taxRate, taxAmount, total, processing: false, invoice: null });
  };

  const confirmSubscribe = async (viaCashfree = false) => {
    const plan = subPay.plan;
    if (!plan) return;
    setSubPay(s => ({ ...s, processing: true }));
    try {
      if (viaCashfree) {
        // Top up total (price + tax)
        const { data: topupData } = await api.post("/payments/cashfree/create-order", {
          amount: subPay.total, target_user_id: child?.id,
        });
        const cfg = await api.get("/payments/config");
        if (cfg.data.cashfree_configured && !topupData.is_mock && window.Cashfree && topupData.payment_session_id) {
          const cf = window.Cashfree({ mode: cfg.data.env === "PRODUCTION" ? "production" : "sandbox" });
          const result = await cf.checkout({ paymentSessionId: topupData.payment_session_id, redirectTarget: "_modal" });
          if (result.error) { toast.error(result.error.message || "Payment cancelled"); setSubPay(s => ({ ...s, processing: false })); return; }
        } else {
          await api.post(`/payments/cashfree/mock-success/${topupData.cf_order_id}`);
        }
        toast.success(`₹${subPay.total} added to wallet`);
      }
      const { data } = await api.post("/meal-plans/subscribe", { student_id: child?.id, plan_id: plan.id });
      setSubPay(s => ({ ...s, processing: false, invoice: {
        plan_name: plan.name, subtotal: plan.price, tax_rate: subPay.taxRate, tax_amount: subPay.taxAmount, total: subPay.total,
        student_name: child?.name, payment_method: viaCashfree ? "Cashfree → Wallet" : "Wallet",
        ref_id: data.student_plan?.id || "—",
      }}));
      toast.success("Meal plan activated!");
      refresh(); load();
    } catch (e) {
      toast.error(formatErr(e));
      setSubPay(s => ({ ...s, processing: false }));
    }
  };

  const openDietary = async (sid) => {
    try {
      const { data } = await api.get(`/parental/restrictions/${sid}`);
      setDietary({ open: true, target: sid, data: data.restrictions || {} });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openAutoTopup = async (sid) => {
    try {
      const { data } = await api.get(`/wallet/auto-topup/${sid}`);
      setAutoTopup({ open: true, target: sid, data });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveAutoTopup = async () => {
    try {
      await api.put(`/wallet/auto-topup/${autoTopup.target}`, autoTopup.data);
      toast.success(autoTopup.data.enabled ? "Auto top-up enabled" : "Auto top-up disabled");
      setAutoTopup({ open: false, target: null, data: null });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveDietary = async () => {
    try {
      await api.put(`/parental/restrictions/${dietary.target}`, dietary.data);
      toast.success("Dietary controls updated");
      setDietary({ open: false, target: null, data: null });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const child = children.find(c => c.id === activeChild);
  const isSubscriber = upcomingBookings.some(b => b.plan_type !== "daily" && b.booking_type !== "special" && (b.live_status === "active" || b.live_status === "upcoming"));

  return (
    <AppShell variant={user?.role === "student" ? "student" : "parent"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Dashboard</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink mt-2">
              Hi {user?.name?.split(" ")[0]}, lunchtime is sorted.
            </h1>
          </div>
          <Link to="/parent/preorder" data-testid="cta-preorder"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink text-white font-semibold hover:bg-ink-700 active:scale-95 self-start md:self-auto">
            <UtensilsCrossed size={16} /> Pre-order tomorrow's meal
          </Link>
        </div>

        {festivals.length > 0 && (
          <div className="mb-5 bg-gradient-to-r from-brand to-sun rounded-3xl p-6 text-white relative overflow-hidden" data-testid="festival-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0"><PartyPopper size={22} /></div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em]">Today at the canteen</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{festivals[0].name}</div>
                <div className="text-sm text-white/90 mt-1">{festivals[0].description}</div>
              </div>
            </div>
          </div>
        )}

        {specials.length > 0 && (
          <div className="mb-6" data-testid="specials-strip">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-amber-600" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Festival & special menus</span>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {specials.map(sp => (
                <div key={sp.id} className="flex-shrink-0 w-72 bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-soft" data-testid={`special-${sp.id}`}>
                  {sp.image_url ? (
                    <img src={sp.image_url} alt="" className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-600"><Sparkles size={28} /></div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold">{sp.scheduled_date || sp.lunch_date}</span>
                      <span className="text-[10px] text-ink-500">{sp.meal_slot || "lunch"}</span>
                    </div>
                    <div className="font-heading text-base font-extrabold text-ink mt-1 truncate">{sp.name || sp.title}</div>
                    <div className="text-[10px] text-ink-500 line-clamp-1 mt-0.5">{(sp.items || []).map(i => `${i.qty}x ${i.name}`).join(" · ")}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="font-heading text-lg font-extrabold text-ink">{inr(sp.price)}<span className="text-[10px] font-normal text-ink-500 ml-1">+GST</span></div>
                      <button data-testid={`order-special-${sp.id}`} onClick={() => setOrderingSpecial(sp)}
                        className="px-3 py-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold hover:bg-amber-600 active:scale-95">
                        Book now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {user?.role === "parent" && children.length > 1 && (
          <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar pb-1">
            {children.map(c => (
              <button key={c.id} onClick={() => setActiveChild(c.id)} data-testid={`child-tab-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${activeChild === c.id ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
                {c.name} <span className="opacity-60">· {c.grade}</span>
                {c.is_subscriber && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />}
              </button>
            ))}
          </div>
        )}

        {/* Upcoming Bookings */}
        {upcomingBookings.filter(b => b.plan_type === "daily" || b.booking_type === "special").length > 0 && (
          <div className="mb-5 bg-white rounded-2xl border border-border p-5" data-testid="upcoming-bookings">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-blue-600" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Upcoming pre-orders</span>
            </div>
            <div className="space-y-3">
              {upcomingBookings.filter(b => b.plan_type === "daily" || b.booking_type === "special").map(b => (
                <div key={b.id} className="flex items-center gap-3 py-3 px-3 rounded-xl bg-blue-50 border border-blue-100">
                  {b.image_url && (
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-cream-200">
                      <img src={b.image_url} alt={b.plan_name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-ink">{b.plan_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                        b.booking_type === "special" ? "bg-amber-200 text-amber-800" :
                        b.plan_type === "daily" ? "bg-blue-200 text-blue-800" : "bg-emerald-200 text-emerald-800"
                      }`}>{b.booking_type === "special" ? "FESTIVAL" : b.plan_type === "daily" ? "PRE-ORDER" : "SUBSCRIPTION"}</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-0.5">{b.start_date}{b.end_date && b.end_date !== b.start_date ? ` → ${b.end_date}` : ""}</div>
                    {b.nutrition && b.booking_type !== "special" && (
                      <div className="text-[10px] text-ink-500 mt-0.5">
                        {b.nutrition.calories} kcal · {b.nutrition.protein}g protein · {b.nutrition.carbs}g carbs · {b.nutrition.fat}g fat
                      </div>
                    )}
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-200 text-emerald-800 text-[9px] font-bold shrink-0">{b.status}</span>
                  {b.plan_type === "daily" && b.status === "active" && (
                    <button data-testid={`cancel-preorder-${b.id}`} onClick={async () => {
                      if (!b.order_id) { toast.error("This pre-order is missing its order reference — please cancel from My Orders."); return; }
                      if (!window.confirm("Cancel this pre-order? Wallet will be refunded.")) return;
                      try { await api.post(`/orders/${b.order_id}/cancel`); toast.success("Cancelled & refunded"); window.location.reload(); } catch(e) { toast.error(formatErr(e)); }
                    }} className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold shrink-0 hover:bg-red-200">Cancel</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Active subscriptions as cards OR wallet card */}
          {isSubscriber ? (
            <div className="md:col-span-5 space-y-3">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Active &amp; upcoming subscriptions</div>
              {upcomingBookings.filter(b => b.plan_type !== "daily" && b.booking_type !== "special" && (b.live_status === "active" || b.live_status === "upcoming")).map((plan, idx) => {
                const isUpcoming = plan.live_status === "upcoming";
                return (
                <div key={plan.id || idx}
                  className={`rounded-2xl p-5 border ${isUpcoming ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`}
                  data-testid={`active-plan-card-${idx}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${isUpcoming ? "bg-blue-200 text-blue-800" : "bg-emerald-200 text-emerald-800"}`}>
                      {isUpcoming ? "UPCOMING" : "ACTIVE"}
                    </span>
                    {plan.term && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider">{plan.term}</span>}
                    <span className={`text-[10px] ${isUpcoming ? "text-blue-600" : "text-emerald-600"}`}>{(plan.slots_covered || plan.slots || []).join(", ")}</span>
                  </div>
                  <div className="font-heading text-lg font-bold text-ink mt-2">{plan.plan_name}</div>
                  <div className="text-sm text-ink-500 mt-1">
                    {isUpcoming
                      ? <>Starts <b className="text-ink">{plan.start_date}</b></>
                      : <>Valid until <b className="text-ink">{plan.end_date}</b></>}
                  </div>
                  {plan.nutrition && <div className={`text-[10px] mt-1 ${isUpcoming ? "text-blue-700" : "text-emerald-700"}`}>{plan.nutrition.calories} kcal · {plan.nutrition.protein}g protein</div>}
                  {!isUpcoming && <WeeklyMenuBtn planId={plan.plan_id} />}
                </div>
                );
              })}
              <div className="flex flex-wrap gap-2 mt-2">
                <Link to="/profile" data-testid="dietary-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 text-ink-700 font-semibold text-xs hover:bg-cream-300">
                  <Shield size={14} /> Dietary controls
                </Link>
                <Link to="/parent/reports" data-testid="see-history"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-200 text-ink-700 font-semibold text-xs hover:bg-cream-300">
                  <BarChart3 size={14} /> History
                </Link>
              </div>
            </div>
          ) : (
            <div className="md:col-span-5 bg-white rounded-3xl p-7 border border-border shadow-soft">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">{user?.role === "parent" ? `${child?.name?.split(' ')[0] || 'Wallet'}'s Wallet` : "My Wallet"}</div>
                  <div className="font-heading text-5xl font-extrabold tracking-tight text-ink mt-3" data-testid="wallet-balance">{inr(child?.wallet_balance)}</div>
                  <div className="text-sm text-ink-500 mt-1">Available balance</div>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-cream-200 flex items-center justify-center text-brand"><Wallet size={22} /></div>
              </div>
              {child?.wallet_balance < 100 && (
                <div className="mt-4 flex items-center gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-sm">
                  <AlertTriangle size={16} /> Balance low — top up to keep meals running.
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                {instashopOn && (
                  <button data-testid="topup-btn" onClick={() => setTopup({ open: true, target: child?.id, amount: 500, processing: false })}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">
                    <Plus size={16} /> Top up
                  </button>
                )}
                <button data-testid="dietary-btn" onClick={() => openDietary(child.id)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-border text-ink-700 hover:border-ink hover:text-ink font-semibold">
                  <Shield size={16} /> Dietary controls
                </button>
                {instashopOn && (
                  <button data-testid="auto-topup-btn" onClick={() => openAutoTopup(child.id)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-border text-ink-700 hover:border-ink hover:text-ink font-semibold">
                    <Zap size={16} /> Auto top-up
                  </button>
                )}
                <Link to="/parent/reports" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-border text-ink-700 hover:border-ink hover:text-ink font-semibold" data-testid="see-history">
                  <BarChart3 size={16} /> History
                </Link>
              </div>
            </div>
          )}

          {/* Pre-order link */}
          {!isSubscriber && (
            <div className="md:col-span-7 bg-gradient-to-br from-ink to-ink-700 text-white rounded-3xl p-7 shadow-soft relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-sun/20 blur-2xl" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-sun">Meal plans</div>
              <div className="font-heading text-2xl font-bold mt-2">Your meal plans are managed by the school</div>
              <div className="text-sm text-white/70 mt-1">Contact your school admin to subscribe to a meal plan. You can pre-order daily meals below.</div>
              {plans.some(p => p.type === "daily") && (
                <Link to="/parent/preorder" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold">
                  <UtensilsCrossed size={14} /> Browse daily menu & pre-order
                </Link>
              )}
            </div>
          )}

          {isSubscriber && (
            <div className="md:col-span-7 bg-white rounded-3xl p-7 border border-border">
              <div className="flex items-center gap-2 text-ink-500 text-xs font-bold uppercase tracking-[0.2em]"><ScanLine size={14} /> Tap to eat</div>
              <div className="font-heading text-2xl font-bold text-ink mt-2">{child?.name}, your card is ready.</div>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cream-200 font-mono text-sm">{child?.rfid_card_id || "RFID not assigned"}</div>
              <div className="mt-4 text-sm text-ink-600">One meal per slot per day · breakfast or lunch are auto-detected at the POS.</div>
              <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Pre-orders</div>
                  <div className="text-sm text-ink-600">Order individual meals for specific dates via the Pre-order page.</div>
                </div>
                <Link to="/parent/preorder" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100">
                  <UtensilsCrossed size={14} /> Pre-order
                </Link>
              </div>
            </div>
          )}

          {/* Allergens */}
          <div className="md:col-span-4 bg-white rounded-3xl p-6 border border-border shadow-soft">
            <div className="flex items-center gap-2 text-ink-500 text-xs font-bold uppercase tracking-[0.2em]"><Sparkles size={14} /> Safety</div>
            <div className="font-heading text-xl font-bold text-ink mt-2">Allergens flagged</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(child?.allergens?.length ? child.allergens : ["—"]).map((a, i) => (
                a === "—" ? <span key={i} className="text-sm text-ink-500">No allergens recorded</span> : <AllergenBadge key={i} tag={a} />
              ))}
            </div>
            <button onClick={() => openDietary(child.id)} className="mt-3 text-xs font-semibold text-brand hover:text-brand-700 inline-flex items-center gap-1">
              <Settings2 size={12} /> Manage controls
            </button>
            <p className="text-xs text-ink-500 mt-3 leading-relaxed">POS blocks any item that violates these rules.</p>
          </div>

          {/* Savings widget removed — parent portal no longer surfaces spend/savings KPIs. */}

          {/* Meals taken — last 7 days (count only, no ₹ amounts) */}
          <div className="md:col-span-12 bg-white rounded-3xl p-6 border border-border shadow-soft">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Last 7 days</div>
                <div className="font-heading text-xl font-bold text-ink mt-1">Meals taken</div>
              </div>
              <div className="text-right">
                <div className="font-heading text-2xl font-extrabold text-ink">{report?.order_count || 0}</div>
                <div className="text-xs text-ink-500">total meals</div>
              </div>
            </div>
            <div className="h-48 mt-4">
              {report?.daily?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78716C" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716C" }} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "#FCEAEA" }} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }} />
                    <Bar dataKey="count" fill="#D12D2D" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-ink-500">No meals yet</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Top-up modal */}
      {topup.open && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => !topup.processing && setTopup({ ...topup, open: false })}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-floating" onClick={(e) => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold text-ink">Top up wallet</div>
            <div className="text-sm text-ink-500 mt-1">Powered by Cashfree · falls back to instant credit if not configured.</div>
            <div className="mt-5 grid grid-cols-3 gap-2">
              {QUICK_AMOUNTS.map(v => (
                <button key={v} onClick={() => setTopup({ ...topup, amount: v })} disabled={topup.processing}
                  className={`py-2 rounded-xl font-semibold ${topup.amount === v ? "bg-ink text-white" : "bg-cream-200 text-ink-700 hover:bg-cream-300"} disabled:opacity-50`}
                  data-testid={`topup-quick-${v}`}>
                  ₹{v}
                </button>
              ))}
            </div>
            <input data-testid="topup-amount" type="number" min="10" value={topup.amount}
              onChange={(e) => setTopup({ ...topup, amount: e.target.value })} disabled={topup.processing}
              className="mt-4 w-full rounded-xl border border-border px-4 py-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            <div className="mt-3 px-3 py-2 rounded-xl bg-cream-200 text-[11px] text-ink-600 leading-snug">
              <span className="font-bold uppercase tracking-wider text-ink-700 mr-1">Demo mode</span>
              Cashfree credentials aren't configured for this school. Use the buttons below to walk through both the success and failure paths — no real money moves.
            </div>
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <button onClick={() => setTopup({ ...topup, open: false })} disabled={topup.processing}
                className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200 disabled:opacity-50">
                Cancel
              </button>
              <button data-testid="topup-fail" onClick={() => doTopup(true)} disabled={topup.processing}
                className="px-5 py-2.5 rounded-full bg-red-50 text-red-700 font-semibold hover:bg-red-100 active:scale-95 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                Simulate failure
              </button>
              <button data-testid="topup-confirm" onClick={() => doTopup(false)} disabled={topup.processing}
                className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {topup.processing ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : <>Pay ₹{topup.amount} <ArrowRight size={14} /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dietary controls modal */}
      {dietary.open && dietary.data && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setDietary({ open: false, target: null, data: null })}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg shadow-floating max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]"><Shield size={14} /> Parental controls</div>
            <div className="font-heading text-2xl font-bold text-ink mt-2">Dietary restrictions</div>
            <p className="text-sm text-ink-500 mt-1">POS will block any purchase that violates these rules.</p>

            <div className="mt-5">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Block items containing</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TAGS.map(t => {
                  const active = dietary.data.allergens?.includes(t);
                  return (
                    <button key={t} data-testid={`diet-allergen-${t}`}
                      onClick={() => setDietary({ ...dietary, data: { ...dietary.data, allergens: active ? dietary.data.allergens.filter(x=>x!==t) : [...(dietary.data.allergens||[]), t] }})}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase border ${active ? 'bg-brand text-white border-brand' : 'bg-white text-ink-700 border-border'}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <label data-testid="diet-veg-only" className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer">
                <input type="checkbox" checked={!!dietary.data.veg_only} onChange={e => setDietary({ ...dietary, data: { ...dietary.data, veg_only: e.target.checked }})} />
                <span className="font-semibold text-sm">Vegetarian only</span>
              </label>
              <label data-testid="diet-vegan-only" className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer">
                <input type="checkbox" checked={!!dietary.data.vegan_only} onChange={e => setDietary({ ...dietary, data: { ...dietary.data, vegan_only: e.target.checked }})} />
                <span className="font-semibold text-sm">Vegan only</span>
              </label>
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Junk-food limit (per week)</label>
              <input data-testid="diet-junk-limit" type="number" min="0" placeholder="No limit"
                value={dietary.data.junk_food_limit_per_week ?? ""}
                onChange={e => setDietary({ ...dietary, data: { ...dietary.data, junk_food_limit_per_week: e.target.value ? Number(e.target.value) : null }})}
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>

            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Block categories (comma-sep)</label>
              <input data-testid="diet-blocked-cats" placeholder="Beverages, Desserts"
                value={(dietary.data.blocked_categories || []).join(", ")}
                onChange={e => setDietary({ ...dietary, data: { ...dietary.data, blocked_categories: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }})}
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setDietary({ open: false, target: null, data: null })} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="diet-save" onClick={saveDietary} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 inline-flex items-center gap-2">
                <CheckCircle2 size={14} /> Save controls
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto top-up modal */}
      {autoTopup.open && autoTopup.data && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setAutoTopup({ open: false, target: null, data: null })}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-floating" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.2em]"><Zap size={14}/> Auto top-up</div>
            <div className="font-heading text-2xl font-bold text-ink mt-2">Never run out of lunch money</div>
            <p className="text-sm text-ink-500 mt-1">Auto-credit the wallet when balance drops below the threshold. Charged from your saved Cashfree card (mock-credits in dev).</p>

            <label data-testid="auto-enabled" className="flex items-center gap-2 p-3 bg-cream-100 rounded-xl cursor-pointer mt-5">
              <input type="checkbox" checked={!!autoTopup.data.enabled}
                onChange={e => setAutoTopup({ ...autoTopup, data: { ...autoTopup.data, enabled: e.target.checked }})} />
              <span className="font-semibold text-sm">Enable auto top-up</span>
            </label>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Trigger when below ₹</label>
                <input data-testid="auto-threshold" type="number" min="0" value={autoTopup.data.threshold || 0}
                  onChange={e => setAutoTopup({ ...autoTopup, data: { ...autoTopup.data, threshold: Number(e.target.value) }})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Top up by ₹</label>
                <input data-testid="auto-amount" type="number" min="0" value={autoTopup.data.amount || 0}
                  onChange={e => setAutoTopup({ ...autoTopup, data: { ...autoTopup.data, amount: Number(e.target.value) }})}
                  className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setAutoTopup({ open: false, target: null, data: null })} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="auto-save" onClick={saveAutoTopup} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95">Save</button>
            </div>
          </div>
        </div>
      )}

      {orderingSpecial && (
        <SpecialOrderModal sp={orderingSpecial}
          children={children.filter(c => user?.role === "parent" ? true : c.id === user?.id)}
          parentRole={user?.role}
          onClose={() => setOrderingSpecial(null)}
          onOrdered={() => { setOrderingSpecial(null); load(); toast.success("Special booked!"); }} />
      )}
      <PWAInstallNudge trigger="login" />

      {/* Subscription Payment Modal */}
      {subPay.open && subPay.plan && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-tall overflow-hidden">
            {subPay.invoice ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Subscription confirmed</div>
                  <button onClick={() => setSubPay({ open: false, plan: null, processing: false, invoice: null })} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
                </div>
                <div className="text-center mb-5">
                  <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
                  <div className="font-heading text-2xl font-extrabold text-ink">{inr(subPay.invoice.total)}</div>
                  <div className="text-sm text-ink-600 mt-1">{subPay.invoice.plan_name}</div>
                </div>
                <div className="bg-cream-100 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-ink-500">Student</span><span className="font-semibold">{subPay.invoice.student_name}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Subtotal</span><span className="font-semibold">{inr(subPay.invoice.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">GST ({subPay.invoice.tax_rate}%)</span><span className="font-semibold">{inr(subPay.invoice.tax_amount)}</span></div>
                  <div className="flex justify-between border-t border-border pt-2"><span className="font-bold">Total paid</span><span className="font-bold">{inr(subPay.invoice.total)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Payment</span><span className="font-semibold">{subPay.invoice.payment_method}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Status</span><span className="font-semibold text-emerald-700">Paid</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">Reference</span><span className="font-mono text-xs">{subPay.invoice.ref_id}</span></div>
                </div>
                <button onClick={() => setSubPay({ open: false, plan: null, processing: false, invoice: null })}
                  className="mt-5 w-full px-5 py-3 rounded-full bg-brand text-white font-semibold">Done</button>
              </div>
            ) : (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-brand">Subscribe to plan</div>
                  <button onClick={() => setSubPay({ open: false, plan: null, processing: false, invoice: null })} className="p-2 rounded-full hover:bg-cream-200"><X size={16} /></button>
                </div>
                <div className="text-center mb-5">
                  <div className="font-heading text-xl font-extrabold text-ink">{subPay.plan.name}</div>
                  <div className="text-sm text-ink-500 mt-1">{child?.name} · Full calendar month</div>
                </div>
                {/* Price breakdown */}
                <div className="bg-cream-100 rounded-xl p-4 mb-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-ink-500">Subtotal</span><span className="font-semibold text-ink">{inr(subPay.plan.price)}</span></div>
                  <div className="flex justify-between"><span className="text-ink-500">GST ({subPay.taxRate}%)</span><span className="font-semibold text-ink">{inr(subPay.taxAmount)}</span></div>
                  <div className="flex justify-between border-t border-border pt-2"><span className="font-bold text-ink">Total</span><span className="font-heading text-xl font-extrabold text-ink">{inr(subPay.total)}</span></div>
                </div>
                <div className="bg-cream-100 rounded-xl p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm"><Wallet size={16} className="text-brand" /><span>Wallet</span></div>
                  <span className="font-heading font-extrabold">{inr(child?.wallet_balance || 0)}</span>
                </div>
                {(child?.wallet_balance || 0) >= subPay.total ? (
                  <button onClick={() => confirmSubscribe(false)} disabled={subPay.processing}
                    className="w-full px-5 py-3.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    <Wallet size={16} /> {subPay.processing ? "Processing..." : `Pay ${inr(subPay.total)} from wallet`}
                  </button>
                ) : (
                  <>
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-sm text-amber-800">
                      <AlertTriangle size={16} /> Insufficient balance. Need <b className="ml-1">{inr(subPay.total - (child?.wallet_balance || 0))}</b> more.
                    </div>
                    <button onClick={() => confirmSubscribe(true)} disabled={subPay.processing}
                      className="w-full px-5 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 disabled:opacity-50 flex items-center justify-center gap-2">
                      <CreditCard size={16} /> {subPay.processing ? "Processing..." : `Top up ${inr(subPay.total)} & subscribe`}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function SpecialOrderModal({ sp, children, parentRole, onClose, onOrdered }) {
  const [studentId, setStudentId] = useState(children?.[0]?.id || "");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const subtotal = (sp.price || 0) * qty;
  const taxRate = 5;
  const taxAmount = Math.round(subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const selectedChild = (children || []).find(c => c.id === studentId) || children?.[0];
  const walletBalance = selectedChild?.wallet_balance || 0;
  const shortBy = Math.max(0, total - walletBalance);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { qty: Number(qty) || 1 };
      if (parentRole === "parent") body.student_id = studentId;
      await api.post(`/specials/${sp.id}/order`, body);
      onOrdered();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const topupAndPay = async () => {
    setBusy(true);
    try {
      // 1. Top up the student's wallet to cover the gap (mock Cashfree → instant credit)
      const targetUserId = parentRole === "parent" ? studentId : selectedChild?.id;
      const cfg = await api.get("/payments/cashfree/config").catch(() => ({ data: { cashfree_configured: false } }));
      const { data: topupData } = await api.post("/payments/cashfree/create-order", {
        amount: Number(shortBy), target_user_id: targetUserId,
      });
      if (cfg.data.cashfree_configured && !topupData.is_mock && window.Cashfree && topupData.payment_session_id) {
        const cf = window.Cashfree({ mode: cfg.data.mode || "sandbox" });
        const result = await cf.checkout({ paymentSessionId: topupData.payment_session_id, redirectTarget: "_modal" });
        if (result.error) throw new Error(result.error.message || "Payment cancelled");
      } else {
        await api.post(`/payments/cashfree/mock-success/${topupData.cf_order_id}`);
      }
      // 2. Place the special order — backend deducts from wallet
      const body = { qty: Number(qty) || 1 };
      if (parentRole === "parent") body.student_id = studentId;
      await api.post(`/specials/${sp.id}/order`, body);
      toast.success(`Topped up ${inr(shortBy)} and booked ${sp.name}`);
      onOrdered();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md flex flex-col max-h-[95vh] sm:max-h-[90vh]" data-testid="special-order-modal">
        <div className="overflow-y-auto px-6 sm:px-7 py-6 flex-1">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700 inline-flex items-center gap-1"><Sparkles size={12}/> Festival / Special menu</div>
        <div className="font-heading text-2xl font-bold text-ink mt-1">{sp.name || sp.title}</div>
        <div className="text-xs text-ink-500 mt-1">{sp.scheduled_date || sp.lunch_date} · {inr(sp.price)} per cover</div>
        {sp.description && <div className="text-sm text-ink-700 mt-2">{sp.description}</div>}
        {(sp.items || []).length > 0 && (
          <div className="bg-cream-100 rounded-2xl p-3 mt-3 text-xs">
            <ul className="space-y-0.5">
              {sp.items.map((i, idx) => <li key={idx}>· {i.qty}x {i.name}</li>)}
            </ul>
          </div>
        )}

        {parentRole === "parent" && children.length > 0 && (
          <div className="mt-4">
            <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">For child</label>
            <select data-testid="special-child" value={studentId} onChange={e => setStudentId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none">
              {children.map(c => <option key={c.id} value={c.id}>{c.name}{c.grade ? ` · ${c.grade}` : ""}</option>)}
            </select>
          </div>
        )}

        <div className="mt-3">
          <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">Qty (covers)</label>
          <input data-testid="special-qty" type="number" min={1} max={20} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value) || 1))}
            className="mt-1 w-full rounded-xl border border-border px-3 py-2 focus:border-brand focus:outline-none" />
        </div>

        <div className="bg-cream-100 rounded-2xl p-4 mt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-ink-500">Subtotal</span><span className="font-semibold">{inr(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-ink-500">GST ({taxRate}%)</span><span className="font-semibold">{inr(taxAmount)}</span></div>
          <div className="flex justify-between border-t border-border pt-2"><span className="font-bold text-ink">Total</span><span className="font-heading text-xl font-extrabold text-ink">{inr(total)}</span></div>
        </div>

        {/* Wallet status */}
        <div className="bg-cream-50 border border-cream-300 rounded-xl p-3 mt-3 flex items-center justify-between text-sm" data-testid="special-wallet-row">
          <div className="flex items-center gap-2">
            <Wallet size={16} className="text-brand" />
            <span className="text-ink-700">Wallet balance{children?.length > 1 ? ` · ${selectedChild?.name || ""}` : ""}</span>
          </div>
          <span className={`font-heading font-extrabold ${shortBy > 0 ? "text-rose-700" : "text-ink"}`}>{inr(walletBalance)}</span>
        </div>

        {shortBy > 0 && (
          <div className="mt-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800">
            <AlertTriangle size={14} />
            <span>Short by <b>{inr(shortBy)}</b> — we'll top up your wallet first.</span>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2 sticky bottom-0 bg-white pt-3 pb-0 -mx-1">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
          {shortBy === 0 ? (
            <button data-testid="special-confirm" disabled={busy} onClick={submit}
              className="px-5 py-2.5 rounded-full bg-amber-500 text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Pay {inr(total)} from wallet
            </button>
          ) : (
            <button data-testid="special-topup-pay" disabled={busy} onClick={topupAndPay}
              className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <CreditCard size={14}/>} Top up {inr(shortBy)} & pay
            </button>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}


function WeeklyMenuBtn({ planId }) {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!planId) return;
    setLoading(true);
    api.get(`/weekly-menu/current?plan_id=${planId}`).then(r => setMenu(r.data?.found ? r.data : null)).catch(() => setMenu(null)).finally(() => setLoading(false));
  }, [planId]);

  if (loading) return <div className="text-[10px] text-ink-400 mt-2">Loading menu...</div>;
  if (!menu) return <div className="text-[10px] text-ink-400 mt-2">No weekly menu uploaded</div>;

  const isTemplate = menu.source === "template";
  const days = isTemplate ? (menu.days || ["mon","tue","wed","thu","fri"]) : null;
  const slots = isTemplate ? (menu.slots || ["breakfast","lunch","snacks"]) : null;
  const dayLabel = (d) => ({ mon:"Mon", tue:"Tue", wed:"Wed", thu:"Thu", fri:"Fri", sat:"Sat", sun:"Sun" }[d] || d);

  const openPdf = () => {
    if (menu.pdf_data) {
      const w = window.open();
      if (w) {
        w.document.write(`<html><head><title>Weekly Menu - ${menu.plan_name || 'Menu'}</title></head><body style="margin:0;padding:0;"><iframe src="${menu.pdf_data}" style="width:100%;height:100vh;border:none"></iframe></body></html>`);
        w.document.close();
      }
    }
  };

  return (
    <div className="mt-3 p-3 rounded-xl bg-emerald-100 border border-emerald-200" data-testid="weekly-menu-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-emerald-700" />
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Weekly Menu</span>
            {menu.title && <span className="text-[10px] text-emerald-700">· {menu.title}</span>}
          </div>
          <div className="text-sm text-emerald-700 mt-1">
            {menu.start_date} — {menu.end_date}
          </div>
          {menu.updated_by && <div className="text-[10px] text-emerald-600 mt-0.5">Uploaded by: {menu.updated_by}</div>}
        </div>
        {!isTemplate && menu.pdf_data && (
          <button
            data-testid="view-weekly-menu-pdf"
            onClick={openPdf}
            className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 active:scale-95 inline-flex items-center gap-1.5"
          >
            <FileText size={12} /> View PDF
          </button>
        )}
      </div>

      {/* Template grid (days × slots) */}
      {isTemplate && days && slots && (
        <div className="mt-3 overflow-x-auto" data-testid="weekly-menu-grid">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr>
                <th className="text-left px-2 py-1.5 text-emerald-800 font-bold uppercase tracking-wider text-[10px]">Day</th>
                {slots.map(s => (
                  <th key={s} className="text-left px-2 py-1.5 text-emerald-800 font-bold uppercase tracking-wider text-[10px] capitalize">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map(d => (
                <tr key={d} className="border-t border-emerald-200/60 align-top">
                  <td className="px-2 py-1.5 font-bold text-emerald-900 whitespace-nowrap">{dayLabel(d)}</td>
                  {slots.map(s => {
                    const cell = (menu.cells || {})[`${d}_${s}`];
                    const items = cell?.items || [];
                    return (
                      <td key={s} className="px-2 py-1.5 text-ink-700">
                        {items.length > 0 ? items.join(", ") : <span className="text-ink-400">—</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {menu.notes && (
            <div className="mt-2 text-[10px] text-emerald-700 italic">Note: {menu.notes}</div>
          )}
        </div>
      )}

      {menu.nutrition && Object.keys(menu.nutrition).length > 0 && (
        <div className="mt-3 pt-3 border-t border-emerald-200/60" data-testid="weekly-menu-nutrition">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mb-2">Nutrition per meal</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(menu.nutrition).map(([slot, vals]) => {
              if (!vals) return null;
              const parts = [];
              if (vals.calories) parts.push(`${vals.calories} kcal`);
              if (vals.protein)  parts.push(`${vals.protein}g protein`);
              if (vals.carbs)    parts.push(`${vals.carbs}g carbs`);
              if (vals.fat)      parts.push(`${vals.fat}g fat`);
              if (parts.length === 0 && !vals.notes) return null;
              return (
                <div key={slot} className="bg-white/70 rounded-lg px-2 py-1.5 border border-emerald-200">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 capitalize">{slot}</div>
                  {parts.length > 0 && <div className="text-[11px] text-ink-700">{parts.join(" · ")}</div>}
                  {vals.notes && <div className="text-[10px] text-ink-500 italic mt-0.5">{vals.notes}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
